import type React from "react";
import { useState, useEffect, useMemo, useCallback } from "react";
import {
  Typography,
  Box,
  Paper,
  FormControl,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  useTheme,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useTranslation } from "react-i18next";
import { getLogislySpxOnCallStatisticsApi } from "../api/logislySpxOnCallApi";
import type { TopRoute } from "../types/logislySpxOnCall";
import LoadingState from "../components/common/LoadingState";
import SyncableChip from "../components/common/SyncableChip";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import type translations from "../translations/translations.json";

type TranslationKeys = keyof typeof translations.en;

type SortField = "route" | "count" | "minPay" | "maxPay" | "avgPay";
type SortOrder = "asc" | "desc";

const formatPrice = (price: number) => {
  return new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    minimumFractionDigits: 0,
  }).format(price);
};

const formatRoute = (route: string[]): string => {
  return route.join(" -> ");
};

const formatDateForApi = (date: Date | null): string => {
  if (!date) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const SimpleBarChart: React.FC<{ data: TopRoute[] }> = ({ data }) => {
  const theme = useTheme();
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [mousePosition, setMousePosition] = useState<{ x: number; y: number } | null>(null);
  const chartHeight = 400;
  const chartWidth = 800;
  const padding = { top: 20, right: 20, bottom: 80, left: 50 };
  const innerWidth = chartWidth - padding.left - padding.right;
  const innerHeight = chartHeight - padding.top - padding.bottom;

  const maxCount = Math.max(...data.map((d) => d.count), 1);
  const barWidth = innerWidth / data.length;
  const barSpacing = barWidth * 0.2; // 20% spacing between bars
  const actualBarWidth = barWidth - barSpacing;

  const handleMouseMove = (event: React.MouseEvent<SVGSVGElement>) => {
    if (hoveredIndex !== null) {
      const rect = event.currentTarget.getBoundingClientRect();
      const svgX = event.clientX - rect.left;
      const svgY = event.clientY - rect.top;
      // Use pixel coordinates relative to the container for more accurate positioning
      setMousePosition({ x: svgX, y: svgY });
    }
  };

  return (
    <Box sx={{ width: "100%", height: chartHeight, position: "relative" }}>
      <svg
        width="100%"
        height={chartHeight}
        viewBox={`0 0 ${chartWidth} ${chartHeight}`}
        onMouseMove={handleMouseMove}
        onMouseLeave={() => {
          setHoveredIndex(null);
          setMousePosition(null);
        }}
        role="img"
        aria-label="Route count bar chart"
      >
        <title>Route count bar chart</title>
        {/* Grid lines */}
        {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
          const y = padding.top + innerHeight - ratio * innerHeight;
          return (
            <line
              key={ratio}
              x1={padding.left}
              y1={y}
              x2={padding.left + innerWidth}
              y2={y}
              stroke={theme.palette.divider}
              strokeWidth={0.5}
              strokeDasharray="2,2"
            />
          );
        })}

        {/* Y-axis labels */}
        {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
          const y = padding.top + innerHeight - ratio * innerHeight;
          const value = Math.round(ratio * maxCount);
          return (
            <text
              key={ratio}
              x={padding.left - 10}
              y={y + 4}
              textAnchor="end"
              fontSize="10"
              fill={theme.palette.text.secondary}
            >
              {value}
            </text>
          );
        })}

        {/* Bars */}
        {data.map((item, index) => {
          const barHeight = (item.count / maxCount) * innerHeight;
          const x = padding.left + index * barWidth + barSpacing / 2;
          const y = padding.top + innerHeight - barHeight;
          const routeLabel = formatRoute(item.route);
          const isHovered = hoveredIndex === index;

          return (
            <g key={index}>
              <rect
                x={x}
                y={y}
                width={actualBarWidth}
                height={barHeight}
                fill={isHovered ? theme.palette.primary.dark : theme.palette.primary.main}
                rx={2}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => {
                  setHoveredIndex(null);
                  setMousePosition(null);
                }}
                style={{ cursor: "pointer", transition: "fill 0.2s" }}
              />
              {/* Bar value label on top */}
              <text
                x={x + actualBarWidth / 2}
                y={y - 5}
                textAnchor="middle"
                fontSize="10"
                fill={theme.palette.text.primary}
                fontWeight="bold"
              >
                {item.count}
              </text>
              {/* X-axis labels */}
              <text
                x={x + actualBarWidth / 2}
                y={chartHeight - padding.bottom + 15}
                textAnchor="middle"
                fontSize="8"
                fill={theme.palette.text.secondary}
                transform={`rotate(-45 ${x + actualBarWidth / 2} ${chartHeight - padding.bottom + 15})`}
              >
                {routeLabel.length > 20 ? `${routeLabel.substring(0, 20)}...` : routeLabel}
              </text>
            </g>
          );
        })}
      </svg>
      {/* Tooltip */}
      {hoveredIndex !== null && mousePosition && (
        <Box
          sx={{
            position: "absolute",
            top: mousePosition.y - 10,
            left: mousePosition.x,
            transform: "translateX(-50%)",
            bgcolor: "rgba(0, 0, 0, 0.8)",
            color: "white",
            padding: "4px 8px",
            borderRadius: 1,
            fontSize: "0.75rem",
            pointerEvents: "none",
            zIndex: 1000,
            whiteSpace: "nowrap",
          }}
        >
          {formatRoute(data[hoveredIndex].route)}
        </Box>
      )}
    </Box>
  );
};

const LogislySpxOnCallStatisticsPage: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [startDate, setStartDate] = useState<Date | null>(() => {
    const date = new Date();
    date.setDate(1); // First day of current month
    return date;
  });
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const [statistics, setStatistics] = useState<TopRoute[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sortField, setSortField] = useState<SortField>("count");
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");

  const fetchStatistics = useCallback(async () => {
    if (!startDate || !endDate) {
      setError("Please select both start and end dates");
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await getLogislySpxOnCallStatisticsApi(
        formatDateForApi(startDate),
        formatDateForApi(endDate),
      );
      setStatistics(response.data.statistics.topRoutes);
    } catch (err) {
      console.error("Error fetching statistics:", err);
      setError("Failed to load statistics");
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    if (startDate && endDate) {
      fetchStatistics();
    }
  }, [startDate, endDate, fetchStatistics]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const sortedStatistics = useMemo(() => {
    const sorted = [...statistics];
    sorted.sort((a, b) => {
      let aValue: string | number;
      let bValue: string | number;

      switch (sortField) {
        case "route":
          aValue = formatRoute(a.route).toLowerCase();
          bValue = formatRoute(b.route).toLowerCase();
          break;
        case "count":
          aValue = a.count;
          bValue = b.count;
          break;
        case "minPay":
          aValue = a.minPay;
          bValue = b.minPay;
          break;
        case "maxPay":
          aValue = a.maxPay;
          bValue = b.maxPay;
          break;
        case "avgPay":
          aValue = a.avgPay;
          bValue = b.avgPay;
          break;
        default:
          return 0;
      }

      if (typeof aValue === "string" && typeof bValue === "string") {
        return sortOrder === "asc" ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      }

      return sortOrder === "asc"
        ? (aValue as number) - (bValue as number)
        : (bValue as number) - (aValue as number);
    });
    return sorted;
  }, [statistics, sortField, sortOrder]);

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return null;
    return sortOrder === "asc" ? (
      <ArrowUpwardIcon fontSize="small" />
    ) : (
      <ArrowDownwardIcon fontSize="small" />
    );
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 1 }}>
        {t("Logisly SPX OnCall Statistics" as TranslationKeys)}
      </Typography>

      {/* Date Range Selector */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(3, 1fr)",
            },
            gap: 2,
            alignItems: "center",
          }}
        >
          <FormControl fullWidth>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label={t("Start Date" as TranslationKeys)}
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                slotProps={{
                  textField: {
                    size: "small",
                    required: true,
                  },
                }}
              />
            </LocalizationProvider>
          </FormControl>
          <FormControl fullWidth>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label={t("End Date" as TranslationKeys)}
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                slotProps={{
                  textField: {
                    size: "small",
                    required: true,
                  },
                }}
              />
            </LocalizationProvider>
          </FormControl>
          <Button
            variant="contained"
            onClick={fetchStatistics}
            disabled={!startDate || !endDate || loading}
            fullWidth
          >
            {t("Load Statistics" as TranslationKeys)}
          </Button>
        </Box>
      </Paper>

      {error && (
        <Paper sx={{ p: 2, mb: 4, bgcolor: "error.light" }}>
          <Typography color="error">{error}</Typography>
        </Paper>
      )}

      {loading && <LoadingState />}

      {!loading && statistics.length > 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {/* Top - Bar Chart */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {t("Route Count Chart" as TranslationKeys)}
            </Typography>
            <SimpleBarChart data={sortedStatistics} />
          </Paper>

          {/* Bottom - Table */}
          <Box>
            <Typography variant="h5" sx={{ mb: 2 }}>
              {t("Top Routes" as TranslationKeys)}
            </Typography>
            <TableContainer
              component={Paper}
              sx={{
                boxShadow: "none",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
              }}
            >
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        cursor: "pointer",
                        "&:hover": { backgroundColor: "action.hover" },
                      }}
                      onClick={() => handleSort("route")}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        {t("Route" as TranslationKeys)}
                        {getSortIcon("route")}
                      </Box>
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        fontWeight: "bold",
                        cursor: "pointer",
                        "&:hover": { backgroundColor: "action.hover" },
                      }}
                      onClick={() => handleSort("count")}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          justifyContent: "flex-end",
                        }}
                      >
                        {t("Count" as TranslationKeys)}
                        {getSortIcon("count")}
                      </Box>
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        fontWeight: "bold",
                        cursor: "pointer",
                        "&:hover": { backgroundColor: "action.hover" },
                      }}
                      onClick={() => handleSort("minPay")}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          justifyContent: "flex-end",
                        }}
                      >
                        {t("Min Pay" as TranslationKeys)}
                        {getSortIcon("minPay")}
                      </Box>
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        fontWeight: "bold",
                        cursor: "pointer",
                        "&:hover": { backgroundColor: "action.hover" },
                      }}
                      onClick={() => handleSort("maxPay")}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          justifyContent: "flex-end",
                        }}
                      >
                        {t("Max Pay" as TranslationKeys)}
                        {getSortIcon("maxPay")}
                      </Box>
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        fontWeight: "bold",
                        cursor: "pointer",
                        "&:hover": { backgroundColor: "action.hover" },
                      }}
                      onClick={() => handleSort("avgPay")}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          justifyContent: "flex-end",
                        }}
                      >
                        {t("Avg Pay" as TranslationKeys)}
                        {getSortIcon("avgPay")}
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedStatistics.map((route, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Box
                          sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.5 }}
                        >
                          {route.route.map((location, locIndex) => (
                            <Box
                              key={locIndex}
                              sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                            >
                              <SyncableChip label={location} />
                              {locIndex < route.route.length - 1 && (
                                <ArrowForwardIcon
                                  sx={{ fontSize: "0.875rem", color: "text.secondary" }}
                                />
                              )}
                            </Box>
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell align="right">{route.count}</TableCell>
                      <TableCell align="right">{formatPrice(route.minPay)}</TableCell>
                      <TableCell align="right">{formatPrice(route.maxPay)}</TableCell>
                      <TableCell align="right">{formatPrice(route.avgPay)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      )}

      {!loading && statistics.length === 0 && !error && (
        <Paper sx={{ p: 3 }}>
          <Typography color="text.secondary">
            {t("No statistics available for the selected date range" as TranslationKeys)}
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default LogislySpxOnCallStatisticsPage;
